import { FC, useEffect, useMemo, useRef, useState } from "react";
import storeStyles from './styles/StoreCreatorModal.module.scss'
import { StoreCreatorModal } from ".";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import DefaultDictionaryCommandsPanel from "../../../components/commandsPanels/DefaultDictionaryCommandsPanel";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import { IStoreViewDTO } from "../../../libs/coreapi-dto/dirs/store";
import { StoreDataProvider } from "../../../Services/DataProviders/StoreDataProvider";
import useGridFilter, { CreateSelectorGridFilter, CreateSelectorGridFilterParam } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { checkAccessStatus, checkLockStatus } from "../../CommonHelperFunctions";
import { OptionsSno } from "./OptionsSno";
import GridWrapper from "../../../components/controls/GridWrapper";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import { Checkbox } from "../../../components/checkboxes/checkbox";
import { usePluginContext } from "../../../system/providers/plugin";
import { debounce } from "lodash";
import { Input } from "antd";
import { SearchIcon } from "../../../libs/corporate/icons/outlined/action/MainCollection";
import { useSupplierSelection } from "../../../system/hooks/useSupplierSelection";
import { sortBySelectedIdGlobal } from "../../../system/hooks/useCombinedData";
import { PopoverReport } from "../../../components/popoverReport/PopoverReport";

interface IStore extends IGridProps {
    isDetail?: boolean
    applyUserFilter?: boolean
    permission?: IPermission
    documentType?: string
}

const MIN_SEARCH_LENGTH = 3;

const StoreGrid: FC<IStore> = (props) => {
    const appCtx = useAppContext();
    const pluginContext = usePluginContext()
    const userContext = useUserContext();
    const storeDataProvider = new StoreDataProvider(appCtx.coreApiService)

    const [data, setData] = useState<IStoreViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");

    const getGridFilter = (): IGridFilter => {
        if (props.idTable) {
            let idTableGridFilter: IGridFilter = CreateSelectorGridFilterParam(props.idTable, props.isEnabledTo);
    
            if (props.baseGridFilter && props.baseGridFilter.columnFilters?.length as number > 0) {
                const baseGridColumns = props.baseGridFilter.columnFilters;
                idTableGridFilter.columnFilters = baseGridColumns.concat(idTableGridFilter.columnFilters);
            }
            return idTableGridFilter;
        } else {
            return props.baseGridFilter ?? CreateSelectorGridFilter;
        }
    }
    const [gridFilter, dispatchGridFilter] = useGridFilter(getGridFilter());
    const [expression, setExpression] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<IGridRow | undefined>(undefined);
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    
    const [onlyUsers, setOnlyUsers] = useState<boolean>(false);
    const lockOnlyUsers = checkLockStatus("MovementOwnContractors", userContext.userPermission)

    const [onlyLegalEntity, setOnlyLegalEntity] = useState<boolean>(
        props.documentType === 'RequestForMovement' ||
        (props.documentType === 'Movement' && !!props.applyUserFilter)
    );
    const legalEntityNamesRef = useRef<string[]>([]);
    const isFirstOnlyUsersRender = useRef(true);
    const isFirstLegalEntityRender = useRef(true);
    const isFirstGridFilterRender = useRef(true);
    const hasLegalEntityNames = useRef(false);

    const [searchValue, setSearchValue] = useState<string>('');
    const isRealRefresh = useRef(false);
    const [isSupplierSelection] = useSupplierSelection();

    const { t } = useTranslation();

    const handleItemsChange = (newItems: IGridRow[]) => {
        setSelectedItems(newItems);
        props?.onMultipleSelect?.(newItems);
    };
    
    useEffect(() => {
        const timer = setTimeout(() => {
            if (isSupplierSelection === true) {
                dispatchGridFilter({
                    type: 'addColumnFilter',
                    payload: {
                        name: 'isAcceptRequestForMovement',
                        operator: 'Eq',
                        value: true,
                    },
                });
            } 
        }, 300);
        return () => clearTimeout(timer);
    }, [isSupplierSelection]);

    useEffect(() => {
        if (isFirstOnlyUsersRender.current) {
            isFirstOnlyUsersRender.current = false;
            return;
        }

        if (onlyUsers) {
            dispatchGridFilter({
                type: 'paramSimpleFilter',
                payload: { gridParamFilter: {...gridFilter.paramFilter, idUserGlobal: userContext.idGlobal}, gridColumnFilter: gridFilter.columnFilters }
            });
        } else {
            dispatchGridFilter({ type: 'deleteSpecificParamFilter', payload: 'idUserGlobal'})
        }
    }, [onlyUsers]);

    useEffect(() => {
        if (pluginContext.masterGrid.selectedItem?.idGlobal && props.isDetail) getView()
    },[pluginContext.masterGrid.selectedItem?.idGlobal])

    useEffect(() => {
        if (isFirstGridFilterRender.current) {
            isFirstGridFilterRender.current = false;
            if (onlyLegalEntity && !hasLegalEntityNames.current) {
                return;
            }
        }

        if (onlyLegalEntity && !hasLegalEntityNames.current) {
            return;
        }

        if (props.idTable || onlyUsers) {
            getViewParam();
            return;
        }

        getView();
    }, [gridFilter])

    useEffect(() => {
        if (viewState === 'refresh') {
            if (props.idTable || onlyUsers) getViewParam();
            else if (!onlyLegalEntity || legalEntityNamesRef.current.length > 0) getView();
        }
    }, [viewState])

    useEffect(() => {
        if (props.dataFromPluginGridSelectorInput) {
            setSelectedItems(props.dataFromPluginGridSelectorInput);
        }
    }, [props.dataFromPluginGridSelectorInput]);

    useEffect(() => {
       
        if (isFirstLegalEntityRender.current) {
            isFirstLegalEntityRender.current = false;
            if (onlyLegalEntity) {
                const userStoresFilter: IGridFilter = {
                    ...CreateSelectorGridFilter,
                    numberPerPage: 10000,
                    pageNumber: 1,
                    paramFilter: { idUserGlobal: userContext.idGlobal }
                };
                storeDataProvider.getViewParam(userStoresFilter, (entities) => {
                    const names = [...new Set(
                        entities.map(e => e.legalEntityName).filter((n): n is string => !!n)
                    )];
                    legalEntityNamesRef.current = names;
                    hasLegalEntityNames.current = true;

                    if (props.idTable || onlyUsers) getViewParam();
                    else getView();
                });
            } else {
                legalEntityNamesRef.current = [];
                hasLegalEntityNames.current = true;
            }
            return;
        }

        if (onlyLegalEntity) {
            const userStoresFilter: IGridFilter = {
                ...CreateSelectorGridFilter,
                numberPerPage: 10000,
                pageNumber: 1,
                paramFilter: { idUserGlobal: userContext.idGlobal }
            };
            storeDataProvider.getViewParam(userStoresFilter, (entities) => {
                const names = [...new Set(
                    entities.map(e => e.legalEntityName).filter((n): n is string => !!n)
                )];
                legalEntityNamesRef.current = names;
                hasLegalEntityNames.current = true;

                if (props.idTable || onlyUsers) getViewParam();
                else getView();
            });
        } else {
            legalEntityNamesRef.current = [];
            hasLegalEntityNames.current = true;
            if (props.idTable || onlyUsers) getViewParam();
            else getView();
        }
    }, [onlyLegalEntity])

    const buildLegalEntityExpression = (): string | null => {
        if (!onlyLegalEntity || legalEntityNamesRef.current.length === 0) return null;
        const escapedNames = legalEntityNamesRef.current.map(n => `'${n.replace(/'/g, "''")}'`).join(', ');
        return `"LegalEntityName" in (${escapedNames})`;
    };

    const applyLegalEntityExpression = (filter: IGridFilter, overrideExpression?: string): IGridFilter => {
        const baseExpr = overrideExpression !== undefined ? overrideExpression : filter.expression;
        const legalEntityExpr = buildLegalEntityExpression();
        if (!legalEntityExpr) return { ...filter, expression: baseExpr };
        return {
            ...filter,
            expression: baseExpr ? `(${baseExpr}) and (${legalEntityExpr})` : legalEntityExpr
        };
    };

    const getView = () => {
        setIsSubmitting(true)

        let expression = (gridFilter as IGridCustomWhereExpressionFilter).expression

        if (pluginContext.masterGrid.selectedItem?.idGlobal && props.isDetail) {
            expression = `"IdContractorGlobal" = '${pluginContext.masterGrid.selectedItem?.idGlobal}'`
        }

        storeDataProvider.getView(applyLegalEntityExpression(gridFilter, expression), (data, totalCount) => {
            const sortedData = sortBySelectedIdGlobal(
                data,
                props.dataFromPluginGridSelectorInput
            );
            setData(sortedData.map((x) => {
                return {
                    ...x,
                    snoName: OptionsSno().find(c => c.value === x.sno)?.displayName ?? 'Не указано'
                } as IStoreViewDTO
            }))
            setTotalCount(totalCount)
            setViewState('view')
            data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData)
            setIsSubmitting(false)
        })
    }

    const getViewParam = () => {
        setIsSubmitting(true)
        storeDataProvider.getViewParam(applyLegalEntityExpression(gridFilter), (data, totalCount) => {
            setData(data.map((x) => {
                return {
                    ...x,
                    snoName: OptionsSno().find(c => c.value === x.sno)?.displayName ?? 'Не указано'
                } as IStoreViewDTO
            }))
            setTotalCount(totalCount)
            setViewState('view')
            data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData)
            setIsSubmitting(false)
        })
    }

    const clearSelectedItems = () => {
        setSelectedItems([]);
        setSelectedItem(undefined);
        props?.onMultipleSelect?.([]);
        props?.onSelect?.(undefined);
    }

    function toggleMultipleSelect(toggleAction: boolean) {
        clearSelectedItems();
        props.setMultipleSelect?.(toggleAction);
    }
    
    const searchStores = (value: string) => {
        if (value.length >= MIN_SEARCH_LENGTH) {
            const expression = `lower("Name") like lower('%${value}%') or lower("ContractorName") like lower('%${value}%') or lower("LegalEntityName") like lower('%${value}%')`;
            setExpression(expression)
            dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: expression });
        } else if (value.length === 0) {
            dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
            setExpression('')
        }
    };

    const debouncedSearch = useMemo(
        () => debounce(searchStores, 1250),
        []
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        
        if (props.isModal) {
            props.onFocusChanged?.(value.length > 0);
        }
        
        if (value.length === 0) {
            debouncedSearch.cancel();
            dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
            setExpression('')
        }
    };

    const handlePressEnter = () => {
        debouncedSearch.cancel();
        if (searchValue.length >= MIN_SEARCH_LENGTH || searchValue.length === 0) {
            searchStores(searchValue);
        }
    };

    useEffect(() => {
        if (searchValue !== '' && searchValue.length >= MIN_SEARCH_LENGTH) {
            debouncedSearch(searchValue);
        }
    }, [searchValue]);

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);
   
    useEffect(() => {
        if (viewState === 'refresh' && isRealRefresh.current) {
            isRealRefresh.current = false;
        }
        setViewState('view');
    }, [viewState]);

    return (
        <PluginWrapper>
            { !props.isDetail &&
                checkAccessStatus(props.plugin.permission as IPermission, userContext.userPermission) && <ToolbarWrapper>
                    <GridWrapper cols={props.isModal ? 3 : 8}>
                        <DefaultDictionaryCommandsPanel
                            selectedItem={[selectedItem, setSelectedItem]}
                            selectedItems={[selectedItems, setSelectedItems]}
                            multipleSelect={[props.multipleSelect, toggleMultipleSelect]}
                            gridFilter={[gridFilter, dispatchGridFilter]}
                            dataProvider={storeDataProvider}
                            creatorModalJsx={StoreCreatorModal}
                            pluginSettings={props.plugin}
                            setViewState={(vs) => {
                                isRealRefresh.current = true;
                                setViewState(vs);
                            }}
                            showGroupProcessing={props.showGroupProcessing}
                        />
                        <div className={storeStyles.checkBoxOnlyUsersWrapper}>
                            <Checkbox
                                name={`userStores-${uuidv4()}`}
                                label={t("directory.stores.onlyUsers")}//'Собственные склады'
                                onChange={(checked: boolean) => {
                                    setOnlyUsers(checked);
                                }}
                                checked={onlyUsers}
                                disabled={lockOnlyUsers && props.permission === "Movement"}
                            />
                        </div>
                        <div className={storeStyles.checkBoxOnlyUsersWrapper}>
                            <Checkbox
                                name={`legalEntity-${uuidv4()}`}
                                label={t("directory.stores.onlyLegalEntity")}
                                onChange={(checked: boolean) => {
                                    setOnlyLegalEntity(checked);
                                }}
                                checked={onlyLegalEntity}
                            />
                        </div>
                        {props.isModal && props.multipleSelect && props.isSearchForMultyselect && selectedItems.length > 0 ? (
                           <div className={storeStyles.popoverReportWrapper}>
                                <PopoverReport
                                    items={props.dataFromPluginGridSelectorInput ? selectedItems : []}
                                    onItemsChange={handleItemsChange}
                                />
                            </div>
                        ) : null}
                    </GridWrapper>
                </ToolbarWrapper>
                
            }
            {!props.isDetail &&
                <div>
                    <GridWrapper cols={8}>
                        <Input
                            className="custom-input-withPrefix"
                            style={{ width: props.isModal ? 360 : 360 }}
                            value={searchValue}
                            placeholder={t("directory.stores.search")}
                            prefix={<SearchIcon />}
                            onPressEnter={handlePressEnter}
                            onChange={handleSearchChange}
                            onBlur={() => {
                                if (props.isModal) props.onFocusChanged?.(false);
                            }}
                            allowClear
                        />
                    </GridWrapper>
                </div>
            }
            {
                isSubmitting ? <Spinner /> : <DefaultGrid
                    showQuantity
                    gridId={props.gridId}
                    data={data}
                    loadingStatus={loadingStatus}
                    setLoadingStatus={setLoadingStatus}
                    //dataProvider={storeDataProvider}
                    getView={(gridFilter, callback) => {
                        if (props.idTable || onlyUsers) {
                            storeDataProvider.getViewParam(applyLegalEntityExpression(gridFilter), (data, totalCount) => {
                                callback(data, totalCount);
                            });
                        } else {
                            let expression_ = expression
                            if (pluginContext.masterGrid.selectedItem?.idGlobal && props.isDetail) {
                                expression_ = `"IdContractorGlobal" = '${pluginContext.masterGrid.selectedItem?.idGlobal}'`
                            }

                            storeDataProvider.getView(applyLegalEntityExpression(gridFilter, expression_), (data, totalCount) => {
                                callback(data, totalCount);
                            });
                        }
                    }}
                    totalCount={totalCount}
                    filter={gridFilter}
                    separator
                    singleDirectory
                    plugin={props.plugin}
                    selectedItem={selectedItem}
                    selectedItems={selectedItems}
                    multipleSelect={props.multipleSelect}
                    searching
                    isServerFilter
                    onMultipleSelect={(rows) => {
                        setSelectedItems(rows ? rows : []);
                        props?.onMultipleSelect?.(rows ? rows : []);
                    }}
                    onSelect={(row) => {
                        if (row) {
                            setSelectedItem(row)
                            props?.onSelect?.(row, data.find(x => x.idGlobal === row?.idGlobal))
                            pluginContext.masterGrid.onSelectDetailEvent(row);
                        } else {
                            setSelectedItem(undefined)
                            props?.onSelect?.(undefined)
                            pluginContext.masterGrid.onUnselectDetailEvent()
                        }
                    }}
                    onDoubleClick={(i) => props.onDoubleClick?.(i, data.find(x => x.idGlobal === i.idGlobal))}
                    onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                    onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                    onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                    onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                    onEnter={() => setViewState("edit")}
                />
            }
        </PluginWrapper>
    )
}

export default StoreGrid
